/**
 * When a screen crashes, show a way out instead of a blank white page - and
 * tell the owner of the system what happened (core/errors.ts).
 *
 * Deliberately plain: it has to work when anything else might be broken, so it
 * reads no settings, no translations context and no state - just the page's
 * language attribute for its words. Sales already saved on the device are not
 * touched; "Try again" redraws the screen, "Go to the till" reloads the app.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportError } from '../errors.js';

const WORDS = {
  en: {
    title: 'Something went wrong on this screen',
    body: 'Nothing that was already saved is lost. The problem has been reported.',
    retry: 'Try again',
    home: 'Go to the till',
  },
  ar: {
    title: 'صار خطأ بهالشاشة',
    body: 'ولا إشي من المحفوظ ضاع. انبعث تقرير بالمشكلة.',
    retry: 'جرّب مرة ثانية',
    home: 'روح عالصندوق',
  },
};

export class ErrorBoundary extends Component<{ children: ReactNode; home?: string }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    reportError(error, info.componentStack ?? undefined);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    const w = document.documentElement.lang === 'ar' ? WORDS.ar : WORDS.en;
    return (
      <div className="crash" role="alert">
        <div className="crash-card">
          <div className="crash-icon" aria-hidden>!</div>
          <h1>{w.title}</h1>
          <p>{w.body}</p>
          <div className="btn-row" style={{ justifyContent: 'center' }}>
            <button className="btn" onClick={() => this.setState({ failed: false })}>
              {w.retry}
            </button>
            <button className="btn primary" onClick={() => window.location.assign(this.props.home ?? '/')}>
              {w.home}
            </button>
          </div>
        </div>
      </div>
    );
  }
}
